import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@components/Button';
import { ModalBase } from '@components/Modal';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { updateState } from '@helpers/filters';
import { ConversationSorting } from '@constants/conversations';
import { useTranslation, useChannels, useLocations } from '@hooks';
import { FiltersIcon } from 'assets/images/icons';
import { CHANNEL } from '@constants/channels';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 564,
        '@media (max-width: 1024px)': {
            width: '100%',
            height: '100%',
            borderRadius: 0,
            maxHeight: 'none',
        },
    },
    '& .modal-base-content': {
        padding: 16,
    },
});

const FiltersPanel = ({ onApplyFilters, filter, setFilter, assigneesOptions }) => {
    const [isFilterOpened, setFilterOpened] = useState(false);
    const [refetchFilters, setRefetchFilters] = useState(false);

    const { archived } = useSelector(state => state.conversations);

    const isMobile = useMediaQuery('@media (max-width: 1024px)');
    const { t } = useTranslation();
    const { getChannelsByLocationsIds, getChannelOptions, checkTwoWaySms, checkWhatsAppStatus } = useChannels();
    const { getLocationsOptions } = useLocations();

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            onApplyFilters();
        }
    }, [refetchFilters]);

    const options = useMemo(() => {
        //locationsOptions
        const locationsOptions = getLocationsOptions();

        //statusesOptions
        const statusesOptions = [
            { value: 0, label: t('InboxPage.open') },
            { value: 1, label: t('InboxPage.resolved') },
        ];

        //channelsOptions
        const filterCb = c =>
            [CHANNEL.LIVE_CHAT, CHANNEL.FACEBOOK, CHANNEL.INSTAGRAM, CHANNEL.WHATSAPP_BUSINESS].includes(c?.type) ||
            checkTwoWaySms(c) ||
            checkWhatsAppStatus(c);
        const availableChannels = getChannelsByLocationsIds(filter.locations, filterCb);
        const channelsOptions = getChannelOptions(
            {
                in: true,
                smsTwoWay: true,
                withArchivedMultiselect: archived > 0,
                withDisconnected: true,
            },
            availableChannels,
        );

        //sortingOptions
        const sortingOptions = [
            { value: ConversationSorting.ActivityNewestFirst, label: t('InboxPage.activityNewestFirst') },
            { value: ConversationSorting.ActivityOldestFirst, label: t('InboxPage.activityOldestFirst') },
            { value: ConversationSorting.CreatedAtNewestFirst, label: t('InboxPage.createdAtNewestFirst') },
            { value: ConversationSorting.CreatedAtOldestFirst, label: t('InboxPage.createdAtOldestFirst') },
        ];

        return {
            statuses: statusesOptions,
            channels: channelsOptions,
            assignees: assigneesOptions,
            locations: locationsOptions,
            sorting: sortingOptions,
        };
    }, [assigneesOptions, archived, filter]);

    const update = {
        locations: items => {
            const selectedValues = items.length ? items : [];
            const selectedValuesIds = selectedValues.length ? selectedValues.map(item => item.id) : [];
            const isAllChannelsSelected = filter.channels.length === options.channels.length;
            const filterCb = c =>
                [CHANNEL.LIVE_CHAT, CHANNEL.FACEBOOK, CHANNEL.INSTAGRAM, CHANNEL.WHATSAPP_BUSINESS].includes(c?.type) ||
                checkTwoWaySms(c) ||
                checkWhatsAppStatus(c);
            const availableChannels = getChannelsByLocationsIds(selectedValuesIds, filterCb);
            const channelsIds = [...availableChannels, ...(archived > 0 ? [CHANNEL.ARCHIVED] : [])];
            setFilter(prev => ({
                ...prev,
                locations: selectedValuesIds,
                locationsValues: selectedValues,
                channels: updateState(prev?.channels, channelsIds, isAllChannelsSelected),
            }));
        },
        statuses: value => {
            setFilter(prev => ({ ...prev, statuses: value }));
        },
        channels: value => {
            setFilter(prev => ({ ...prev, channels: value }));
        },
        sorting: e => setFilter(prev => ({ ...prev, sorting: e.target.value })),
        timeframe: data => {
            setFilter(prev => ({
                ...prev,
                timeframe: { start: data[0], end: data[1] },
                from: data[0] ? format(new Date(data[0]), 'yyyy-MM-dd') : null,
                to: data[1] ? format(new Date(data[1]), 'yyyy-MM-dd') : null,
            }));
        },
        assignees: value => {
            setFilter(prev => ({ ...prev, assignees: value }));
        },
        showUnreadOnly: e => setFilter(prev => ({ ...prev, showUnreadOnly: e.target.checked })),
    };

    const formOptions = [
        {
            type: 'autocomplete',
            label: t(`filters.companies`),
            value: filter.locationsValues,
            onChange: update.locations,
            options: options.locations,
            error: !filter.locationsValues.length,
        },
        {
            type: 'multiSelect',
            label: t('filters.statuses'),
            value: filter.statuses,
            onChange: update.statuses,
            options: options.statuses,
            countable: true,
            error: !filter.statuses.length,
        },
        {
            type: 'multiSelect',
            label: t('filters.channels'),
            value: filter.channels,
            onChange: update.channels,
            options: options.channels,
            countable: true,
            error: !filter.channels.length,
            nameBased: true,
        },
        {
            type: 'select',
            label: t('filters.sorting'),
            value: filter.sorting,
            onChange: update.sorting,
            options: options.sorting,
        },
        {
            type: 'timeframe',
            label: t('filters.timeframe'),
            value: [filter.timeframe.start, filter.timeframe.end],
            onChange: update.timeframe,
        },
        {
            type: 'multiSelect',
            label: t('common.assignedTo'),
            value: filter.assignees,
            onChange: update.assignees,
            options: options.assignees,
            countable: true,
            groupBased: true,
            error: !filter.assignees.length,
            style: { gridColumn: isMobile ? '1 / span 1' : '1 / span 2' },
        },
        {
            type: 'switch',
            label: t(`filters.showUnreadOnly`),
            value: filter.showUnreadOnly,
            onChange: update.showUnreadOnly,
            style: { gridColumn: isMobile ? '1 / span 1' : '1 / span 2' },
        },
    ];

    const isValid = Object.keys(filter).every(key => {
        if (Array.isArray(filter[key])) {
            return !!filter[key].length;
        } else {
            return true;
        }
    });

    return (
        <>
            <Button
                sx={{
                    minWidth: '40px',
                    marginTop: '10px',
                    '& .MuiButton-startIcon': {
                        margin: 0,
                    },
                }}
                variant="outlined"
                onClick={() => setFilterOpened(!isFilterOpened)}
                startIcon={<FiltersIcon />}
            />
            <Modal
                isOpen={isFilterOpened}
                handleClose={() => setFilterOpened(false)}
                title={t('buttons.filters')}
                onPrimaryAction={() => {
                    setRefetchFilters(true);
                    setFilterOpened(false);
                }}
                primaryActionDisabled={!isValid}
                primaryActionText={t('buttons.applyFilters')}
                onSecondaryAction={() => setFilterOpened(false)}
                secondaryActionText={t('buttons.cancel')}
            >
                <FiltersFormCreator type="inbox" options={formOptions} />
            </Modal>
        </>
    );
};

export default FiltersPanel;
